import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { notification } from "antd";
import { arraysAreEqual, request } from "../../services/cpln";
import { getBillingErrorMessage } from "../../services/utils";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { BillingAccount, BillingAccountAlert, BillingUserRole } from "../../schema/types/account";
import { BillingAccountDetailInput, BillingUserInput } from "./create/input";
import { billingAccountToDetailInput, inputToBillingAccount } from "./utils";
import { getValidatorDetail } from "./validator";
import { getDirtyChecker } from "./dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";
import { AccountDetailContent } from "./detailContent";
import { store } from "../../store";

interface Props {
  account: BillingAccount;
  tou: boolean;
  users: BillingUserInput[];
  roles: BillingUserRole[];
  alert: BillingAccountAlert;
  orgNames: string[];
  hasOrg: boolean;
  fetchPaymentMethods: any;
  fetchAccount: any;
}
export const AccountDetailForm: React.FC<Props> = (props) => {
  const { account, users, fetchAccount } = props;

  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = billingAccountToDetailInput(account, users);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: billingAccountToDetailInput(account, users) }));
  }, [JSON.stringify(account), JSON.stringify(users)]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidatorDetail(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(values: BillingAccountDetailInput) {
    const form = store.getState().form.instances[formId];

    try {
      if (form.dirty.account) {
        const body = {
          account: {
            email: account.email,
            //
            fullName: values.account.fullName,
            accountName: values.account.accountName,
            phone: values.account.phone,
            extraInfo: { ...(account.extraInfo || {}), company: values.account.company },
            address: values.address,
          },
        };
        await request({ service: "billing-ng", url: `/account/${account.id}`, method: "put", body: body });
      }

      if (form.dirty.users) {
        for (const editedUser of values.users || []) {
          if (editedUser.roles.length < 1) {
            continue;
          }

          const user = (users || []).find((u) => u.email === editedUser.email);

          if (user) {
            if (arraysAreEqual(editedUser.roles, user.roles)) {
              continue;
            }
            await request({
              method: "put",
              service: "billing-ng",
              url: `/account/${account.id}/user/email/${editedUser.email}`,
              body: {
                user: {
                  email: editedUser.email,
                  roles: editedUser.roles,
                },
              },
            });
            continue;
          }

          await request({
            method: "post",
            service: "billing-ng",
            url: `/account/${account.id}/user`,
            body: {
              user: {
                email: editedUser.email,
                roles: editedUser.roles,
              },
            },
          });
          continue;
        }

        for (const user of users || []) {
          const editedUser = (values.users || []).find((u) => u.email === user.email);
          if (editedUser) {
            continue;
          }
          await request({
            method: "delete",
            service: "billing-ng",
            url: `/account/${account.id}/user/email/${user.email}`,
          });
        }
      }

      if (form.dirty.alert) {
        const body = {
          alert: {
            enabled: values.alert?.enabled,
            amount: Number(values.alert?.amount),
          },
        };

        // create
        if (!account.alert) {
          await request({ service: "billing-ng", url: `/account/${account.id}/alert`, method: "post", body: body });
        } else {
          // update
          await request({ service: "billing-ng", url: `/account/${account.id}/alert`, method: "put", body: body });
        }
      }

      if (Object.keys(form.dirty).length > 0) {
        notification.success({
          message: "Success",
          description: "Updated billing account",
        });

        fetchAccount();
      }
    } catch (e) {
      notification.warning({
        message: "Failed",
        description: getBillingErrorMessage(e),
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <AccountDetailContent {...props} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail dashboard="home" link={`/account/${account.id}`} inputToItemFn={inputToBillingAccount} />
      <CpPreviewModal inputToItemFn={inputToBillingAccount} kind="account" namePath="name" title="Billing Account" />
      <CpDebugModal inputToItemFn={inputToBillingAccount} kind="account" namePath="name" title="Billing Account" />
    </CpFormContext.Provider>
  );
};
